/**
 * Platform · contact directory.
 *
 * Every stakeholder and contractor the airport works with — a contractor, an
 * agency, an airline, an in-house department — with the people in it and every
 * way to reach each of them: several mobiles, landlines, several emails, a
 * WhatsApp number, each labelled, one of each kind primary, each switched on
 * or off for messages. A person signed in to the mobile app also gets alerts
 * there. This is the directory work order messages are sent from: a rule on
 * "Who is told" names people, organisations or dispatch teams, and they are
 * reached every way that is switched on here.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { useDirectory, useSaveContactOrg, useSaveContactPerson } from "../../api/queries";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Segmented,
  Select,
  Skeleton,
} from "../../design/components";
import {
  checkPoint,
  CHANNEL_LABEL,
  normalisePoints,
  ORG_KIND_LABEL,
  POINT_KIND_LABEL,
  type Channel,
  type ContactOrg,
  type ContactPerson,
  type ContactPoint,
  type OrgKind,
  type PointKind,
} from "../../domain/contacts";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { Dialog } from "../stores/Purchasing";
import s from "./Contacts.module.css";

const ICON: Record<PointKind, string> = { mobile: "📱", phone: "☎", email: "✉", whatsapp: "💬" };
const blankPerson = (orgId?: string): ContactPerson => ({
  name: "",
  orgId,
  language: "ar",
  preferred: "sms",
  active: true,
  points: [{ kind: "mobile", value: "", primary: true, notify: true }],
});

export function ContactsPage() {
  const session = useSession();
  const dir = useDirectory();
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<"all" | OrgKind>("all");
  const [editOrg, setEditOrg] = useState<Omit<ContactOrg, "people"> | null>(null);
  const [editPerson, setEditPerson] = useState<ContactPerson | null>(null);

  const orgs = dir.data?.orgs ?? [];
  const everyone = [...orgs.flatMap((o) => o.people), ...(dir.data?.people ?? [])];
  const points = everyone.reduce((n, p) => n + p.points.length, 0);
  const match = (p: ContactPerson) =>
    !q ||
    [p.name, p.role, ...p.points.map((x) => x.value)].some((v) => (v ?? "").toLowerCase().includes(q.toLowerCase()));
  const shown = orgs
    .filter((o) => kind === "all" || o.kind === kind)
    .map((o) => ({ ...o, people: o.people.filter(match), hit: !q || o.name.toLowerCase().includes(q.toLowerCase()) }))
    .filter((o) => o.hit || o.people.length);
  const kinds = [...new Set(orgs.map((o) => o.kind))];

  useScreenContext({
    route: "/contacts",
    title: "Contact directory",
    summary: [
      t("{o} organizations, {p} people, {n} ways to reach them.", { o: orgs.length, p: everyone.length, n: points }),
    ],
    offers: [{ label: t("Who is told about P1 work?"), ask: "who is told when a P1 work order is raised?" }],
    worksInFull: true,
  });

  return (
    <Page eyebrow={t("{site} · Platform", { site: session.site.code })} title="Contact directory">
      <p className={s.lede}>
        {t(
          "Every stakeholder and contractor, with their people and the contact details used for work order messages.",
        )}{" "}
        <Link to="/notify-rules">{t("Who is told →")}</Link>
      </p>
      {dir.data?.channels && (!dir.data.channels.sms || !dir.data.channels.email) && (
        <p className={s.warn} data-testid="channels-warning">
          {t(
            "SMS and email can't send yet. Messages are saved on each work order until a provider is connected.",
          )}
        </p>
      )}
      <div className={s.bar}>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("search a name, a number, an address")}
          aria-label={t("Search the directory")}
          className={s.search}
        />
        <Segmented
          ariaLabel="Kind"
          value={kind}
          onChange={setKind}
          options={[{ value: "all", label: "All" }, ...kinds.map((k) => ({ value: k, label: ORG_KIND_LABEL[k] }))]}
        />
        <span className={s.count}>
          {t("{o} organizations · {p} people · {n} numbers and addresses", {
            o: orgs.length,
            p: everyone.length,
            n: points,
          })}
        </span>
        <Button variant="primary" onClick={() => setEditOrg({ name: "", kind: "contractor", active: true })}>
          {t("Add an organization")}
        </Button>
        <Button onClick={() => setEditPerson(blankPerson())}>{t("Add a person")}</Button>
      </div>

      {dir.isPending ? (
        <Skeleton rows={6} />
      ) : dir.isError ? (
        <ErrorState error={dir.error} retry={() => void dir.refetch()} />
      ) : shown.length === 0 ? (
        <EmptyState>{t("Nobody matches.")}</EmptyState>
      ) : (
        <div className={s.grid}>
          {shown.map((o) => (
            <Card
              key={o.id}
              title={o.name}
              sub={t(ORG_KIND_LABEL[o.kind])}
              testId={`org-${o.name}`}
              actions={
                <div className={s.actions}>
                  <Button size="sm" onClick={() => setEditPerson(blankPerson(o.id))}>
                    {t("Add a person")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setEditOrg({
                        id: o.id,
                        name: o.name,
                        kind: o.kind,
                        notes: o.notes,
                        active: o.active,
                        siteId: o.siteId,
                      })
                    }
                  >
                    {t("Edit")}
                  </Button>
                </div>
              }
            >
              {o.people.length === 0 ? (
                <p className={s.dim}>{t("Nobody listed yet.")}</p>
              ) : (
                o.people.map((p) => <PersonRow key={p.id} p={p} onEdit={() => setEditPerson(structuredClone(p))} />)
              )}
            </Card>
          ))}
          {(dir.data?.people ?? []).filter(match).length > 0 && kind === "all" && (
            <Card title="Not in an organization">
              {(dir.data?.people ?? []).filter(match).map((p) => (
                <PersonRow key={p.id} p={p} onEdit={() => setEditPerson(structuredClone(p))} />
              ))}
            </Card>
          )}
        </div>
      )}

      {editOrg && <OrgDialog org={editOrg} onClose={() => setEditOrg(null)} />}
      {editPerson && <PersonDialog person={editPerson} orgs={orgs} onClose={() => setEditPerson(null)} />}
    </Page>
  );
}

function PersonRow({ p, onEdit }: { p: ContactPerson; onEdit: () => void }) {
  return (
    <div className={s.person} data-testid={`person-${p.name}`} data-active={p.active}>
      <div className={s.personHead}>
        <b>{p.name}</b>
        {p.role && <span className={s.dim}>{p.role}</span>}
        <span className={s.tag}>{p.language === "ar" ? "عربي" : "English"}</span>
        <span className={s.tag}>{t("prefers {c}", { c: t(CHANNEL_LABEL[p.preferred]) })}</span>
        {p.hours && <span className={s.tag}>{p.hours}</span>}
        {p.principalId && <span className={s.tagOn}>{t("mobile app")}</span>}
        {!p.active && <span className={s.tagOff}>{t("retired")}</span>}
        <button type="button" className={s.linkBtn} onClick={onEdit}>
          {t("Edit")}
        </button>
      </div>
      <div className={s.points}>
        {p.points.map((x, i) => (
          <span
            key={i}
            className={s.point}
            data-notify={x.notify}
            data-primary={x.primary}
            title={x.notify ? t("messages go here") : t("switched off for messages")}
          >
            <span aria-hidden="true">{ICON[x.kind]}</span> {x.value}
            {x.label && <small> · {x.label}</small>}
            {x.primary && <small className={s.star}> ★</small>}
          </span>
        ))}
        {p.points.length === 0 && <span className={s.dim}>{t("no number or address")}</span>}
      </div>
    </div>
  );
}

function OrgDialog({ org, onClose }: { org: Omit<ContactOrg, "people">; onClose: () => void }) {
  const save = useSaveContactOrg();
  const [v, setV] = useState(org);
  return (
    <Dialog
      title={org.id ? t("Edit {name}", { name: org.name }) : t("Add an organization")}
      onClose={onClose}
      testId="org-dialog"
    >
      <Field label="Name">
        <Input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} aria-label={t("Name")} />
      </Field>
      <Field label="Kind">
        <Select value={v.kind} onChange={(e) => setV({ ...v, kind: e.target.value as OrgKind })}>
          {(Object.keys(ORG_KIND_LABEL) as OrgKind[]).map((k) => (
            <option key={k} value={k}>
              {t(ORG_KIND_LABEL[k])}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Notes">
        <Input value={v.notes ?? ""} onChange={(e) => setV({ ...v, notes: e.target.value })} />
      </Field>
      <label className={s.check}>
        <input type="checkbox" checked={v.active} onChange={(e) => setV({ ...v, active: e.target.checked })} />{" "}
        {t("Active")}
      </label>
      {save.isError && <p className={s.bad}>{String(save.error)}</p>}
      <div className={s.actions}>
        <Button onClick={onClose}>{t("Cancel")}</Button>
        <Button
          variant="primary"
          disabled={!v.name.trim() || save.isPending}
          onClick={() => save.mutate(v, { onSuccess: onClose })}
          data-testid="org-save"
        >
          {t("Save")}
        </Button>
      </div>
    </Dialog>
  );
}

function PersonDialog({ person, orgs, onClose }: { person: ContactPerson; orgs: ContactOrg[]; onClose: () => void }) {
  const { source } = useSession();
  const save = useSaveContactPerson();
  const signIns = useQuery({ queryKey: ["admin-people"], queryFn: () => source.adminPeople(), staleTime: 60_000 });
  const [v, setV] = useState(person);
  const problems = useMemo(
    () => v.points.map((p) => (p.value.trim() ? checkPoint(p) : { ok: false as const, why: t("empty") })),
    [v.points],
  );
  const bad = problems.some((p) => !p.ok);
  const setPoint = (i: number, patch: Partial<ContactPoint>) =>
    setV({
      ...v,
      points: v.points.map((p, j) =>
        j === i
          ? { ...p, ...patch }
          : patch.primary && p.kind === (patch.kind ?? v.points[i].kind)
            ? { ...p, primary: false }
            : p,
      ),
    });

  return (
    <Dialog
      title={person.id ? t("Edit {name}", { name: person.name }) : t("Add a person")}
      onClose={onClose}
      testId="person-dialog"
    >
      <div className={s.twoCol}>
        <Field label="Name">
          <Input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} aria-label={t("Name")} />
        </Field>
        <Field label="Organization">
          <Select value={v.orgId ?? ""} onChange={(e) => setV({ ...v, orgId: e.target.value || undefined })}>
            <option value="">{t("— none —")}</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Role">
          <Input value={v.role ?? ""} onChange={(e) => setV({ ...v, role: e.target.value })} />
        </Field>
        <Field label="Hours">
          <Input
            value={v.hours ?? ""}
            onChange={(e) => setV({ ...v, hours: e.target.value })}
            placeholder={t("e.g. 24/7, Sun–Thu 07:00–16:00")}
          />
        </Field>
        <Field label="Language">
          <Select value={v.language} onChange={(e) => setV({ ...v, language: e.target.value as "ar" | "en" })}>
            <option value="ar">العربية</option>
            <option value="en">English</option>
          </Select>
        </Field>
        <Field label="Prefers">
          <Select value={v.preferred} onChange={(e) => setV({ ...v, preferred: e.target.value as Channel })}>
            {(Object.keys(CHANNEL_LABEL) as Channel[]).map((c) => (
              <option key={c} value={c}>
                {t(CHANNEL_LABEL[c])}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Mobile app sign-in">
          <Select
            value={v.principalId ?? ""}
            onChange={(e) => setV({ ...v, principalId: e.target.value || undefined })}
            aria-label={t("Mobile app sign-in")}
          >
            <option value="">{t("— not signed in to the app —")}</option>
            {(signIns.data ?? [])
              .filter((p) => p.kind === "user")
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.displayName}
                  {p.email ? ` · ${p.email}` : ""}
                </option>
              ))}
          </Select>
        </Field>
        <label className={s.check}>
          <input type="checkbox" checked={v.active} onChange={(e) => setV({ ...v, active: e.target.checked })} />{" "}
          {t("Active")}
        </label>
      </div>

      <div className={s.pointsHead}>
        <b>{t("Numbers and addresses")}</b>
        <span className={s.dim}>{t("every one switched on gets the message; one of each kind is primary")}</span>
      </div>
      {v.points.map((p, i) => (
        <div key={i} className={s.pointRow} data-testid={`point-${i}`}>
          <Select
            value={p.kind}
            onChange={(e) => setPoint(i, { kind: e.target.value as PointKind, primary: false })}
            aria-label={t("Kind")}
          >
            {(Object.keys(POINT_KIND_LABEL) as PointKind[]).map((k) => (
              <option key={k} value={k}>
                {t(POINT_KIND_LABEL[k])}
              </option>
            ))}
          </Select>
          <Input
            value={p.value}
            onChange={(e) => setPoint(i, { value: e.target.value })}
            placeholder={p.kind === "email" ? "name@company.com" : "05X XXX XXXX"}
            aria-label={t("Number or address")}
            aria-invalid={!problems[i].ok && !!p.value}
          />
          <Input
            value={p.label ?? ""}
            onChange={(e) => setPoint(i, { label: e.target.value })}
            placeholder={t("label — duty phone, office …")}
            aria-label={t("Label")}
          />
          <label className={s.check} title={t("primary of its kind")}>
            <input
              type="radio"
              checked={p.primary}
              onChange={() => setPoint(i, { primary: true })}
              aria-label={t("Primary")}
            />{" "}
            ★
          </label>
          <label className={s.check}>
            <input
              type="checkbox"
              checked={p.notify}
              onChange={(e) => setPoint(i, { notify: e.target.checked })}
              aria-label={t("Messages")}
            />{" "}
            {t("messages")}
          </label>
          <button
            type="button"
            className={s.linkBtn}
            onClick={() => setV({ ...v, points: v.points.filter((_, j) => j !== i) })}
            aria-label={t("Remove")}
          >
            ✕
          </button>
          {!problems[i].ok && p.value && <span className={s.bad}>{(problems[i] as { why: string }).why}</span>}
        </div>
      ))}
      <div className={s.actions}>
        <Button
          size="sm"
          onClick={() =>
            setV({ ...v, points: [...v.points, { kind: "mobile", value: "", primary: false, notify: true }] })
          }
          data-testid="point-add"
        >
          {t("Add a number or address")}
        </Button>
      </div>
      {save.isError && <p className={s.bad}>{String(save.error)}</p>}
      <div className={s.actions}>
        <Button onClick={onClose}>{t("Cancel")}</Button>
        <Button
          variant="primary"
          disabled={!v.name.trim() || bad || save.isPending}
          data-testid="person-save"
          onClick={() =>
            save.mutate(
              {
                ...v,
                points: normalisePoints(
                  v.points.map((p) => {
                    const c = checkPoint(p);
                    return c.ok ? { ...p, value: c.value } : p;
                  }),
                ),
              },
              { onSuccess: onClose },
            )
          }
        >
          {t("Save")}
        </Button>
      </div>
    </Dialog>
  );
}
